/** Responsive grid/slider enhancement for Vision product lists only.
 * Retain the existing Flickity instance and DOM across breakpoints: no duplicate
 * product-card handlers, no replacement cart controls, no change to mobile swipe.
 */
if (!customElements.get('empyreum-product-layout')) {
  class EmpyreumProductLayout extends HTMLElement {
    connectedCallback() {
      this.slider = document.getElementById(this.dataset.target);
      if (!this.slider || this.observer) return;
      this.schedule = () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.sync());
      };
      this.onResize = () => { this.needsResize = true; this.schedule(); };
      this.onSelect = () => this.updateAccessibility();
      this.onKey = (event) => {
        if (this.grid && event.target === this.slider && ['ArrowLeft', 'ArrowRight'].includes(event.key)) {
          event.stopImmediatePropagation();
        }
      };
      this.slider.addEventListener('keydown', this.onKey, true);
      window.addEventListener('resize', this.onResize);
      this.observer = new MutationObserver((records) => {
        if (records.some((r) => r.type === 'childList' || r.target === this.slider || r.target.classList.contains('columns'))) this.schedule();
      });
      this.observer.observe(this.slider, {childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'data-layout-desktop', 'data-layout-tablet', 'data-mobile-columns']});
      this.schedule();
    }
    sync() {
      if (!this.isConnected || !this.slider) return;
      const f = window.Flickity && Flickity.data(this.slider);
      if (!f) return; // The observer catches Vision's deferred initialization.
      if (this.flickity !== f) {
        if (this.flickity) this.flickity.off('select', this.onSelect);
        this.flickity = f;
        this.originalDraggable = f.options.draggable;
        this.originalTabindex = this.slider.getAttribute('tabindex');
        f.on('select', this.onSelect);
        this.grid = undefined;
      }
      const grid = getComputedStyle(this.slider).getPropertyValue('--ep-grid').trim() === '1';
      const changed = grid !== this.grid;
      this.grid = grid;
      if (changed) {
        f.options.draggable = grid ? false : this.originalDraggable;
        if (typeof f.updateDraggable === 'function') f.updateDraggable();
        if (grid) this.slider.removeAttribute('tabindex');
        else if (this.originalTabindex !== null) this.slider.setAttribute('tabindex', this.originalTabindex);
      }
      if (changed || this.needsResize) {
        this.needsResize = false;
        f.resize();
      }
      // The original Vision script still manages arrow positioning/end states.
      const noOverflow = f.slideableWidth <= f.size.innerWidth + 1;
      this.slider.querySelectorAll('.flickity-nav').forEach((arrow) => { arrow.hidden = grid || noOverflow; });
      this.updateAccessibility();
    }
    updateAccessibility() {
      if (!this.slider || !this.flickity) return;
      const selected = this.flickity.selectedElements || [];
      this.slider.querySelectorAll('.columns').forEach((cell) => {
        const active = cell.classList.contains('carousel__slide');
        if (this.grid && active) cell.removeAttribute('aria-hidden');
        else cell.setAttribute('aria-hidden', String(!active || !selected.includes(cell)));
      });
    }
    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      window.removeEventListener('resize', this.onResize);
      if (this.slider) this.slider.removeEventListener('keydown', this.onKey, true);
      if (this.observer) this.observer.disconnect();
      if (this.flickity) this.flickity.off('select', this.onSelect);
      this.observer = null;
      this.flickity = null;
    }
  }
  customElements.define('empyreum-product-layout', EmpyreumProductLayout);
}
